import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { ConfirmDialog } from '@/features/admin/components/ConfirmDialog';
import { supabase } from '@/shared/services/supabase';
import { useCreateFrame, useUpdateFrame, useDeleteFrame } from '../hooks/useFrames';
import { frameSchema, type FrameFormData } from '../schemas/frameSchema';
import type { FrameReplenishmentItem, Location, Manufacturer } from '@/shared/types/domain.types';

interface FrameEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  frame?: FrameReplenishmentItem; // If provided, we're editing
}

export function FrameEntryForm({ isOpen, onClose, frame }: FrameEntryFormProps) {
  const { showToast } = useToast();
  const isEditing = !!frame;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FrameFormData>({
    resolver: zodResolver(frameSchema),
    defaultValues: frame
      ? {
          location_id: frame.location_id,
          manufacturer_id: frame.manufacturer_id,
          brand: frame.brand,
          frame_name_or_model: frame.frame_name_or_model,
          color: frame.color,
          eye_size: frame.eye_size,
          order_type: frame.order_type,
          patient_name: frame.patient_name || undefined,
          tray_number: frame.tray_number || undefined,
          notes: frame.notes || undefined,
        }
      : undefined,
  });

  const orderType = watch('order_type');
  const selectedManufacturerId = watch('manufacturer_id');

  // Fetch locations
  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Location[];
    },
  });

  // Fetch manufacturers
  const { data: manufacturers, isLoading: manufacturersLoading } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Manufacturer[];
    },
  });

  // Get brands for selected manufacturer
  const selectedManufacturer = manufacturers?.find((m) => m.id === selectedManufacturerId);
  const brands = selectedManufacturer?.brands || [];

  const createMutation = useCreateFrame();
  const updateMutation = useUpdateFrame();
  const deleteMutation = useDeleteFrame();

  // Reset form when modal opens/closes or frame changes
  useEffect(() => {
    if (isOpen) {
      if (frame) {
        reset({
          location_id: frame.location_id,
          manufacturer_id: frame.manufacturer_id,
          brand: frame.brand,
          frame_name_or_model: frame.frame_name_or_model,
          color: frame.color,
          eye_size: frame.eye_size,
          order_type: frame.order_type,
          patient_name: frame.patient_name || undefined,
          tray_number: frame.tray_number || undefined,
          notes: frame.notes || undefined,
        });
      } else {
        reset({
          order_type: 'STOCK',
        });
      }
    }
  }, [isOpen, frame, reset]);

  const onSubmit = async (data: FrameFormData) => {
    try {
      if (isEditing && frame) {
        await updateMutation.mutateAsync({
          id: frame.id,
          ...data,
        });
        showToast('Frame updated successfully', 'success');
      } else {
        await createMutation.mutateAsync(data);
        showToast('Frame added successfully', 'success');
      }
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving frame:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to save frame',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleDelete = async () => {
    if (!frame) return;

    try {
      await deleteMutation.mutateAsync(frame.id);
      showToast('Frame entry deleted successfully', 'success');
      setShowDeleteDialog(false);
      onClose();
      reset();
    } catch (error) {
      console.error('Error deleting frame:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to delete frame',
        'error'
      );
    }
  };

  if (locationsLoading || manufacturersLoading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Loading...">
        <div className="text-center py-4">Loading...</div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Frame Entry' : 'Add Frame Entry'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Location */}
        <Select
          label="Location"
          required
          options={[
            { value: '', label: 'Select a location' },
            ...(locations?.map((l) => ({ value: l.id, label: l.name })) || []),
          ]}
          error={errors.location_id?.message}
          {...register('location_id')}
        />

        {/* Manufacturer */}
        <Select
          label="Manufacturer"
          required
          options={[
            { value: '', label: 'Select a manufacturer' },
            ...(manufacturers?.map((m) => ({ value: m.id, label: m.name })) || []),
          ]}
          error={errors.manufacturer_id?.message}
          {...register('manufacturer_id')}
        />

        {/* Brand */}
        {brands.length > 0 ? (
          <Select
            label="Brand"
            required
            options={[
              { value: '', label: 'Select a brand' },
              ...brands.map((b) => ({ value: b, label: b })),
            ]}
            error={errors.brand?.message}
            {...register('brand')}
          />
        ) : (
          <Input
            label="Brand"
            required
            placeholder="Enter brand name"
            error={errors.brand?.message}
            {...register('brand')}
          />
        )}

        {/* Frame Name/Model and Color */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Frame Name/Model"
            required
            placeholder="e.g., Wayfarer"
            error={errors.frame_name_or_model?.message}
            {...register('frame_name_or_model')}
          />
          <Input
            label="Color"
            required
            placeholder="e.g., Tortoise"
            error={errors.color?.message}
            {...register('color')}
          />
        </div>

        {/* Eye Size */}
        <Input
          label="Eye Size"
          required
          placeholder="e.g., 52-18"
          error={errors.eye_size?.message}
          {...register('eye_size')}
        />

        {/* Order Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="STOCK"
                {...register('order_type')}
                className="mr-2"
              />
              Stock
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="PATIENT"
                {...register('order_type')}
                className="mr-2"
              />
              Patient
            </label>
          </div>
          {errors.order_type && (
            <p className="mt-1 text-sm text-red-600">{errors.order_type.message}</p>
          )}
        </div>

        {/* Patient-specific fields (only shown when order type is PATIENT) */}
        {orderType === 'PATIENT' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
            <Input
              label="Patient Name"
              required
              placeholder="Enter patient name"
              error={errors.patient_name?.message}
              {...register('patient_name')}
            />
            <Input
              label="Tray Number"
              required
              placeholder="Enter tray number"
              error={errors.tray_number?.message}
              {...register('tray_number')}
            />
          </div>
        )}

        {/* Notes */}
        <Textarea
          label="Notes"
          placeholder="Optional notes..."
          rows={3}
          error={errors.notes?.message}
          {...register('notes')}
        />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
          <div>
            {isEditing && (
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full sm:w-auto"
              >
                Delete
              </Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="button" variant="secondary" onClick={handleClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Frame' : 'Add Frame'}
            </Button>
          </div>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Frame Entry"
        message={`Are you sure you want to delete this frame entry? ${frame ? `${frame.brand} ${frame.frame_name_or_model} - ${frame.color}` : ''}`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />
    </Modal>
  );
}
